/**
 * Administration is one nav entry opening a tabbed page.
 *
 * That is the frame's model — the sidebar had eight items in two sections — and
 * it is why every administration screen renders through here rather than
 * reaching for `AppShell` itself.
 *
 * Six tabs, not the frame's three, and no role sees more than five.
 * **Auditoría is a deliberate addition**: the contract serves `GET /audit-events`,
 * and the addendum requires knowing who created and modified each DeCA, so the
 * screen exists and needs a way in. It is recorded in the reconciliation as
 * undrawn rather than quietly dropped. *Empresa* came with #110: a company's own
 * driver permissions and linked phones.
 *
 * *Empresas cliente* is ASGTRANS's own tab and is hidden from an organisation
 * administrator, who has no other company to see; *Empresa* is the company
 * administrator's and is hidden from ASGTRANS, which works for no company.
 * *Cuentas de servicio* (#161) is ASGTRANS's too, and only in its own view: with
 * a client company chosen, a super administrator works inside that company,
 * where the accounts are not. The gates are affordances; the server answers an
 * out-of-role call with 403 whatever is rendered.
 */

import { useEffect, useRef, useSyncExternalStore, type ReactNode } from 'react'
import { Link, useLocation } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { useAuth } from '../app/auth.tsx'
import { abilitiesFor } from '../deca/roles.ts'
import { getOrganisationContext, subscribeOrganisation } from '../app/organisation.ts'
import { AppShell } from '../shell/AppShell.tsx'

interface Tab {
  to: string
  labelKey: string
  platformOnly?: boolean
  companyOnly?: boolean
  /** ASGTRANS's own view only: no client company chosen (#161). */
  asgtransOnly?: boolean
}

const TABS: Tab[] = [
  { to: '/admin/empresas', labelKey: 'nav.organisations', platformOnly: true },
  { to: '/admin/cuentas-de-servicio', labelKey: 'nav.serviceAccounts', platformOnly: true, asgtransOnly: true },
  { to: '/admin/usuarios', labelKey: 'nav.users' },
  { to: '/admin/api', labelKey: 'nav.apiKeys' },
  { to: '/admin/auditoria', labelKey: 'nav.audit' },
  // Last, so the tabs a company administrator already knows keep their places.
  { to: '/admin/mi-empresa', labelKey: 'nav.company', companyOnly: true },
]

export function AdminShell({
  heading,
  subtitle,
  children,
}: {
  /** The page's own heading, scoped to the company where that makes sense. */
  heading?: ReactNode
  subtitle?: ReactNode
  children: ReactNode
}) {
  const { t } = useTranslation()
  const { principal } = useAuth()
  const location = useLocation()
  const abilities = abilitiesFor(principal)
  // Read from the store itself rather than through `useOrganisationChoice`, which needs a query client a
  // shell rendered alone does not have.
  const context = useSyncExternalStore(subscribeOrganisation, getOrganisationContext)
  const tabsRef = useRef<HTMLElement>(null)

  // On a narrow screen the strip scrolls inside itself, and the last tab (*Empresa*) starts out of
  // view: bring the current one in. `?.` because jsdom has no `scrollIntoView`.
  useEffect(() => {
    tabsRef.current
      ?.querySelector('[aria-current="page"]')
      ?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' })
  }, [location.pathname])

  return (
    <AppShell title={t('nav.administration')}>
      <nav className="tabs" aria-label={t('nav.administration')} ref={tabsRef}>
        {TABS.filter(
          (tab) =>
            (!tab.platformOnly || abilities.platformAdmin) &&
            (!tab.companyOnly || abilities.managesCompany) &&
            (!tab.asgtransOnly || !context),
        ).map((tab) => (
          <Link
            key={tab.to}
            to={tab.to}
            className="tabs__tab"
            aria-current={location.pathname === tab.to ? 'page' : undefined}
          >
            {t(tab.labelKey)}
          </Link>
        ))}
      </nav>

      {heading ? (
        <div className="stack-sm">
          <h2 className="admin-heading">{heading}</h2>
          {subtitle ? <p className="small muted">{subtitle}</p> : null}
        </div>
      ) : null}

      {children}
    </AppShell>
  )
}
